import React, { Suspense, lazy, useState, useCallback, use, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, json } from 'react-router-dom';
import { Box, Container, CircularProgress, useMediaQuery, useTheme, IconButton } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import { Menu as MenuIcon } from '@mui/icons-material';
import 'react-toastify/dist/ReactToastify.css';
import Logo from './assets/Logo_BIDV.png';

// Import i18n configuration
import './locales/i18n';

// Import store
import { useAuthStore } from './stores/authStore';
import JWEService from './utils/JWEService.js';

const Sidebar = lazy(() => import('./components/layout/Sidebar.js'));
const UserLogin = lazy(() => import('./components/user/UserLogin.js'));
const UserDashboard = lazy(() => import('./components/user/UserDashboard.js'));
const ProtectedRoute = lazy(() => import('./components/common/ProtectedRoute.js'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 phút
      cacheTime: 10 * 60 * 1000, // 10 phút
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const LoadingSpinner = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="200px"
  >
    <CircularProgress />
  </Box>
);

function App() {
  const { isAuthenticated, userType, userData, forceClearSession, logout } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginRoute = location.pathname === '/user/login';

  // State for mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);


  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleSidebarClose = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Handle click outside to close mobile menu
  const handleOverlayClick = useCallback(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [isMobile, sidebarOpen]);

  // Clear invalid session on app start
  React.useEffect(() => {
    if (isAuthenticated && (!userData || !userData.cardNumber)) {
      console.log('App - Clearing invalid session on startup');
      forceClearSession();
    }

  }, [isAuthenticated, userData, forceClearSession]);

  // Global inactivity timeout: 120s -> redirect to login
  React.useEffect(() => {
    if (!isAuthenticated || isLoginRoute) return;

    let timeoutId;
    const RESET_MS = 120 * 1000;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        try {
          logout();
        } catch (e) {
          // ignore
        }
        navigate('/user/login', { replace: true });
      }, RESET_MS);
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, resetTimer, true));
    resetTimer();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach(evt => window.removeEventListener(evt, resetTimer, true));
    };
  }, [isAuthenticated, isLoginRoute, navigate, logout]);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoadingSpinner />}>
        <Box sx={{
          minHeight: '100vh',
          backgroundColor: 'grey.50',
          display: 'flex',
          flexDirection: 'column'
        }}>

          <Box sx={{
            flex: 1,
            backgroundColor: isLoginRoute ? 'grey.50' : 'white',
            display: 'flex'
          }}>
            {/* Global Sidebar for all pages */}
            <Sidebar open={sidebarOpen} onClose={handleSidebarClose} />
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative', backgroundColor: '#ffffff' }}>
              {/* Mobile Overlay */}
              {isMobile && sidebarOpen && (
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1100,
                  }}
                  onClick={handleOverlayClick}
                />
              )}

              {/* Mobile Header with Hamburger Menu */}
              {isMobile && (
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1100 }}>
                  {/* Top Bar - Logo + Hamburger */}
                  <Box
                    sx={{
                      height: '56px',
                      backgroundColor: '#005452',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      position: 'relative',
                      zIndex: 1300,
                    }}
                  >
                    {/* Logo */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={Logo}
                        alt="BIDV"
                        sx={{
                          height: 32,
                          width: 'auto',
                          mr: 1
                        }}
                      />

                    </Box>

                    {/* Hamburger Menu */}
                    <IconButton
                      onClick={handleSidebarToggle}
                      sx={{
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '8px',
                        padding: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1)'
                        }
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Desktop Header Spacer */}
              {!isMobile && (
                <Box
                  sx={{
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    backgroundColor: '#F7F7F7',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                  }}
                />
              )}

              {isLoginRoute ? (
                <Box sx={{ pt: { xs: 0, md: 0 }, backgroundColor: '#ffffff', }}>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/user/login" replace />} />
                      <Route path="/user/login" element={<UserLogin />} />

                      <Route
                        path="/user/*"
                        element={
                          <ProtectedRoute
                            isAuthenticated={isAuthenticated && userType === 'user'}
                            redirectTo="/user/login"
                          >
                            <UserDashboard />
                          </ProtectedRoute>
                        }
                      />


                      <Route path="*" element={<Navigate to="/user/login" replace />} />
                    </Routes>
                  </Suspense>
                </Box>
              ) : (
                <Container
                  maxWidth="lg"
                  sx={{
                    py: { xs: 4, sm: 5, md: 6 },
                    px: { xs: 2, sm: 3, md: 4 },
                  }}
                >
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/user/login" replace />} />
                      <Route path="/user/login" element={<UserLogin />} />

                      <Route
                        path="/user/*"
                        element={
                          <ProtectedRoute
                            isAuthenticated={isAuthenticated && userType === 'user'}
                            redirectTo="/user/login"
                          >
                            <UserDashboard />
                          </ProtectedRoute>
                        }
                      />


                      <Route path="*" element={<Navigate to="/user/login" replace />} />
                    </Routes>
                  </Suspense>
                </Container>
              )}
            </Box>
          </Box>
        </Box>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

      </Suspense>
    </QueryClientProvider>
  );
}

export default App;
